import { Component, computed, DestroyRef, inject, OnInit, signal, viewChild } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ChartComponent } from 'ng-apexcharts';
import { finalize, forkJoin, map, Observable, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { EGetMembersCountUserType, IMembersCount, UsersService } from 'core/services/users.service';
import { AuthService } from 'core';
import { CountriesService } from 'core/services/countries.service';
import { ThousandsSeparatorPipe } from 'shared';
import { KorviuLoaderComponent } from 'shared/components/korviu-loader/korviu-loader.component';
import { KorviuButtonComponent } from 'shared/components/korviu-button/korviu-button.component';
import { KorviuListComponent } from 'shared/components/korviu-list/korviu-list.component';
import { User } from 'shared/types/user.model';
import { IUser } from 'shared/types/user.interface';
import { IKorviuListConfig, KorviuListItemTypes } from 'shared/types/korviu-list.config';
import { SortOrders } from 'shared/types/sort-order.enum';
import { IPagination } from 'shared/types/pagination.interface';
import { IFilter } from 'shared/types/filter.interface';
import { ISorting } from 'shared/types/sorting.interface';
import { IGetPaginatedResponse } from 'shared/types/get-paginated-response.interface';
import { ChartOptions, DEFAULT_CHART_OPTIONS, IMemberApiResponse } from 'shared/types/chart.config';
import { NavigationService } from 'core/services/navigation.service';
import { EmptyTemplateDirective } from 'shared/directives/empty-template.directive';
import { KorviuNoDataComponent } from 'shared/components/korviu-no-data/korviu-no-data.component';
import { ReferralModalFacade } from '../../../../shared/services/referral-modal.facade';

enum ETab {
  ALL = 'all',
  GANARBOLS = 'ganarbols',
  GANACUATES = 'ganacuates',
  GANASOCIOS = 'ganasocios'
}

const userTypesMap = new Map<ETab, EGetMembersCountUserType>([
  [ETab.ALL, EGetMembersCountUserType.ALL],
  [ETab.GANARBOLS, EGetMembersCountUserType.GANARBOL],
  [ETab.GANACUATES, EGetMembersCountUserType.GANACUATE],
  [ETab.GANASOCIOS, EGetMembersCountUserType.GANASOCIO]
]);

@Component({
  selector: 'app-my-forest-main-info-special',
  standalone: true,
  imports: [
    ThousandsSeparatorPipe,
    TranslatePipe,
    ChartComponent,
    KorviuLoaderComponent,
    KorviuButtonComponent,
    KorviuListComponent,
    EmptyTemplateDirective,
    KorviuNoDataComponent
  ],
  templateUrl: './my-forest-main-info-special.component.html',
  styleUrl: './my-forest-main-info-special.component.scss'
})
export class MyForestMainInfoSpecialComponent implements OnInit {
  readonly myForestList = viewChild<KorviuListComponent>('myForestList');

  authService = inject(AuthService);
  usersService = inject(UsersService);
  translateService = inject(TranslateService);
  destroyRef = inject(DestroyRef);
  router = inject(Router);
  navigationService = inject(NavigationService);
  private readonly referralModalFacade = inject(ReferralModalFacade);
  private readonly countriesService = inject(CountriesService);

  isLoading = signal<boolean>(false);
  readonly emptyListHasFilters = signal(false);

  currentUser = computed<User>(() => this.authService.currentUser);
  currentMonthLocale = computed<string>(() => {
    const months = [
      'months.january',
      'months.february',
      'months.march',
      'months.april',
      'months.may',
      'months.june',
      'months.july',
      'months.august',
      'months.september',
      'months.october',
      'months.november',
      'months.december'
    ];
    return months[new Date().getMonth()];
  });

  leavesNumber = computed<number>(() => this.currentUser()?.leaves ?? 0);
  referralsNumber = computed<number>(() => this.currentUser()?.totalReferrals ?? 0);
  communityNumber = computed<number>(() => this.currentUser()?.totalNetworkSize ?? 0);
  usMembers = signal<number>(0);
  mxMembers = signal<number>(0);

  selectedTab = signal<ETab>(ETab.ALL);
  tabs = computed(() => {
    return [
      { id: ETab.ALL, label: 'my-forest.all' },
      { id: ETab.GANARBOLS, label: 'my-forest.ganarbols' },
      { id: ETab.GANACUATES, label: 'my-forest.ganacuates' },
      { id: ETab.GANASOCIOS, label: 'my-forest.ganasocios' }
    ];
  });

  listConfig: IKorviuListConfig = {
    getFn: this.getMyForest.bind(this),
    sortSections: [
      {
        localizableName: 'my-forest.name',
        options: [
          {
            localizableName: 'common.sortOption.A-Z',
            sortDirection: { sortBy: 'full_name', sortOrder: SortOrders.ASCEND }
          },
          {
            localizableName: 'common.sortOption.Z-A',
            sortDirection: { sortBy: 'full_name', sortOrder: SortOrders.DESCEND }
          }
        ]
      },
      {
        localizableName: 'my-forest.totalLeaves',
        options: [
          {
            localizableName: 'common.sortOption.highest',
            sortDirection: { sortBy: 'leaves', sortOrder: SortOrders.DESCEND }
          },
          {
            localizableName: 'common.sortOption.lowest',
            sortDirection: { sortBy: 'leaves', sortOrder: SortOrders.ASCEND }
          }
        ]
      }
    ],
    filterSections: [],
    filterSectionsGetFn: () =>
      this.countriesService.getCountries(this.translateService.currentLang === 'en' ? 'en' : 'es').pipe(
        map(countries => [
          {
            name: '',
            localizableName: 'my-forest.location',
            parameter_name: 'country',
            selectionMode: 'single' as const,
            checkboxModel: countries
              .filter(country => country.code === 'US' || country.code === 'MX')
              .map(country => ({
                label: country.name,
                localizableName: country.code === 'US' ? 'my-forest.us' : 'my-forest.mx',
                value: String(country.id),
                parameter_name: String(country.id)
              }))
          }
        ])
      ),
    itemType: KorviuListItemTypes.MY_FOREST_SPECIAL,
    detailsFn: this.goToUser.bind(this)
  };

  public membersChartOptions: Partial<ChartOptions>;

  ngOnInit(): void {
    this.isLoading.set(true);
    forkJoin([this._fetchMembers()])
      .pipe(
        finalize(() => {
          this.isLoading.set(false);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    this.#getMembersCount(userTypesMap.get(this.selectedTab()));
  }

  onTabClick(tab: ETab): void {
    if (tab === this.selectedTab()) {
      return;
    }
    this.selectedTab.set(tab);

    this.#getMembersCount(userTypesMap.get(tab));
    this.myForestList()?.reloadData(true);
  }

  goToUser(user: User): void {
    void this.router.navigate(['/workspace/my-forest/user/', user.publicId || user.id]);
  }

  onBalanceDetailsClick(): void {
    this.router.navigate(['/workspace/balance']);
  }

  getMyForest(
    pagination: IPagination,
    filters: IFilter[],
    sorting: ISorting
  ): Observable<IGetPaginatedResponse<User[]>> {
    this.emptyListHasFilters.set(filters.some(filter => !!filter.value));
    filters = [...filters, { value: this.selectedTab(), name: 'user_type' }];

    return this.usersService.getMyForest<IUser[]>(pagination, filters, sorting).pipe(
      map(response => ({
        ...response,
        results: response.results.map(r => new User(r))
      }))
    );
  }

  onGrowYourCommunityClick(): void {
    this.navigationService.setNextGoBackUrl('/workspace/my-forest');
    void this.router.navigate(['/workspace/my-referrals']);
  }

  inviteReferral(): void {
    this.referralModalFacade.openAddReferralModal();
  }

  private _buildMembersChart(response: IMemberApiResponse): void {
    const directData = response.members.map((m: any) => m.direct);
    const networkData = response.members.map((m: any) => m.network);
    const categories = response.members.map((m: any) => m.user_type);
    const BAR_HEIGHT = 32;
    const GAP = 16;
    const numBars = categories.length;
    const totalChartHeight = numBars * BAR_HEIGHT + (numBars + 5) * GAP;
    const isSpanish = this.translateService.currentLang === 'es';
    const directLabel = isSpanish ? 'Referidos directos' : 'Direct Referrals';
    const networkLabel = isSpanish ? 'Miembros de la comunidad' : 'Community Members';
    this.membersChartOptions = {
      ...DEFAULT_CHART_OPTIONS,
      chart: {
        ...DEFAULT_CHART_OPTIONS.chart,
        type: 'bar',
        stacked: true,
        height: totalChartHeight,
        width: '100%',
        toolbar: { show: false }
      },
      plotOptions: {
        ...DEFAULT_CHART_OPTIONS.plotOptions,
        bar: {
          horizontal: true,
          barHeight: `${BAR_HEIGHT}px`,
          borderRadius: 4
        }
      },
      xaxis: {
        categories,
        labels: { show: false },
        axisBorder: { show: false },
        axisTicks: { show: false }
      },
      yaxis: {
        labels: {
          show: true,
          style: {
            fontSize: '16px',
            fontFamily: 'Outfit',
            colors: '#505760'
          }
        }
      },
      series: [
        { name: directLabel, data: directData },
        { name: networkLabel, data: networkData }
      ],
      tooltip: {
        ...DEFAULT_CHART_OPTIONS.tooltip,
        enabled: true,
        shared: false,
        intersect: true,
        custom: ({ series, dataPointIndex, w }) => {
          if (dataPointIndex < 0) return '';

          const userType = w.config.xaxis.categories[dataPointIndex];
          const directVal = series[0][dataPointIndex] ?? 0;
          const networkVal = series[1][dataPointIndex] ?? 0;
          const directColor = w.config.colors[0];
          const networkColor = w.config.colors[1];

          return `
				<div class="px-3 py-2 flex flex-col custom-tooltip">
				  <div class="text-sm md:text-base font-medium outfit title">
					${userType}
				  </div>
				  <div class="paragraph-small-card font-normal outfit value">
					<span class="inline-block mr-2 rounded-sm" style="width: 10px; height: 10px; background-color: ${directColor};" ></span> ${directLabel}: ${directVal}<br/>
            		<span class="inline-block mr-2 rounded-sm" style="width: 10px; height: 10px; background-color: ${networkColor};" ></span> ${networkLabel}: ${networkVal}
				  </div>
				</div>
			  `;
        }
      },
      colors: ['#007DCB', '#8AC3E7'],
      legend: {
        show: true,
        fontSize: '16px',
        fontFamily: 'Outfit',
        fontWeight: '400',
        position: 'bottom',
        labels: {
          colors: ['#505760'],
          useSeriesColors: false
        },
        onItemClick: {
          toggleDataSeries: false
        },
        onItemHover: {
          highlightDataSeries: false
        }
      },
      dataLabels: {
        enabled: false
      }
    };
  }

  private _fetchMembers(): Observable<IMemberApiResponse> {
    const currentUserId = this.currentUser()?.id;
    return this.usersService.getMyForestMembersById(currentUserId).pipe(
      tap((data: IMemberApiResponse) => {
        /* const selectedUserType: UserTypes = this.currentViewedUserType;
          const allowed: string[] = getAllowedUserTypes(selectedUserType);
          let filteredMembers = data.members.filter(m => allowed.includes(m.user_type));
          const hierarchyOrder = ['Ganasocios', 'Ganaliados', 'Ganagentes', 'Ganaquipos', 'Ganarbols', 'Ganacuates'];
          filteredMembers = filteredMembers.sort(
            (a, b) => hierarchyOrder.indexOf(a.user_type) - hierarchyOrder.indexOf(b.user_type)
          ); */
        const hierarchyOrder = ['Ganasocios', 'Ganaliados', 'Ganagentes', 'Ganaquipos', 'Ganarbols', 'Ganacuates'];
        const sortedMembers = data.members.sort(
          (a, b) => hierarchyOrder.indexOf(a.user_type) - hierarchyOrder.indexOf(b.user_type)
        );
        this._buildMembersChart({
          ...data,
          members: sortedMembers
        });
      })
    );
  }

  #getMembersCount(userType: EGetMembersCountUserType): void {
    this.usersService
      .getMembersCount(userType)
      .pipe(
        tap((data: IMembersCount) => {
          this.usMembers.set(data.us);
          this.mxMembers.set(data.mx);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }
}
